<template>
  <AuthenticatedView
    :loading="projectLoading && !projectResult"
    :current-user="projectResult?.currentUser || null"
    :has-result="!!projectResult"
  >
    <div class="card">
      <breadcrumbs :items="breadcrumbsItems" />
      <div class="setup-header">
        <h2>{{ s__('ProjectSetup|Step-by-Step Setup') }}</h2>
        <div class="progress-indicator">
          {{
            sprintf(s__('ProjectSetup|Step %{current} of %{total}'), {
              current: currentStep + 1,
              total: totalSteps,
            })
          }}
        </div>
      </div>

      <div v-if="loading" class="muted">{{ __('Loading…') }}</div>
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else-if="project && isComponentReady" :key="localeKey">
        <div class="step-content">
          <!-- Step 1: Basic Info -->
          <SetupBasicInfo
            v-if="currentStep === 0"
            :key="`step-0-${localeKey}`"
            ref="stepRef"
            :project="project"
            :saving="saving"
            @skip="handleSkip"
          />

          <!-- Step 2: Language & Timezone -->
          <SetupLanguageTimezone
            v-if="currentStep === 1"
            :key="`step-1-${localeKey}`"
            ref="stepRef"
            :project="project"
            :timezones="timezones"
            :detected-timezone="detectedTimezone"
            :loading-timezones="loadingTimezones"
            :saving="saving"
            @skip="handleSkip"
          />

          <!-- Step 2: Notifications (only if has Telegram group) -->
          <SetupNotifications
            v-if="currentStep === notificationsStep && hasTelegramGroup"
            :key="`step-${notificationsStep}-${localeKey}`"
            ref="stepRef"
            :project="project"
            :step-number="notificationsStep + 1"
            :saving="saving"
            @save="handleStepSave"
            @skip="handleSkip"
          />

          <!-- Step 3: Silent Hours (only if has Telegram group) -->
          <SetupSilentHours
            v-if="currentStep === silentHoursStep && hasTelegramGroup"
            ref="stepRef"
            :project="project"
            :step-number="silentHoursStep + 1"
            :timezones="timezones"
            :detected-timezone="detectedTimezone"
            :saving="saving"
            :go-to-step="goToStep"
            @save="handleStepSave"
            @skip="handleSkip"
          />

          <!-- Step 4: Deadline Reports (only if has Telegram group) -->
          <SetupDeadlineReports
            v-if="currentStep === deadlineReportsStep && hasTelegramGroup"
            ref="stepRef"
            :project="project"
            :step-number="deadlineReportsStep + 1"
            :saving="saving"
            @save="handleStepSave"
            @skip="handleSkip"
          />

          <!-- Complete -->
          <SetupComplete v-if="currentStep === completeStep" :tasks-url="tasksUrl" />
        </div>

        <div v-if="currentStep < totalSteps - 1" class="step-navigation">
          <button
            v-if="currentStep > 0"
            type="button"
            class="btn-secondary"
            :disabled="saving"
            @click="goToPreviousStep"
          >
            {{ __('Back') }}
          </button>
          <div class="spacer"></div>
          <template v-if="hasUnsavedChanges">
            <button
              type="button"
              class="btn-secondary"
              :disabled="saving"
              @click="handleResetChanges"
            >
              {{ s__('ProjectSetup|Reset Changes') }}
            </button>
            <button
              type="button"
              class="btn-primary"
              :disabled="saving"
              @click="handleSaveAndContinue"
            >
              {{ saving ? s__('ProjectSetup|Saving...') : s__('ProjectSetup|Save and Continue') }}
            </button>
          </template>
          <template v-else>
            <button type="button" class="btn-secondary" :disabled="saving" @click="handleSkip">
              {{ __('Skip') }}
            </button>
            <button type="button" class="btn-primary" :disabled="saving" @click="handleNext">
              {{ __('Next') }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </AuthenticatedView>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onErrorCaptured } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __, sprintf, loadTranslations } from '@/locale'
import Breadcrumbs from '../common/Breadcrumbs.vue'
import SetupBasicInfo from './setup/SetupBasicInfo.vue'
import SetupLanguageTimezone from './setup/SetupLanguageTimezone.vue'
import SetupNotifications from './setup/SetupNotifications.vue'
import SetupSilentHours from './setup/SetupSilentHours.vue'
import SetupDeadlineReports from './setup/SetupDeadlineReports.vue'
import SetupComplete from './setup/SetupComplete.vue'
import AuthenticatedView from '../common/AuthenticatedView.vue'
import { removeInitialLoading } from '@/utils/loadingScreen'

const props = defineProps({ projectId: String, chatId: String, fromId: String })

console.log('[ProjectSetup] Component initializing', {
  projectId: props.projectId,
  chatId: props.chatId,
  fromId: props.fromId,
})

// Error handler
onErrorCaptured((err, instance, info) => {
  console.error('[ProjectSetup] Error captured', { err, instance, info, stack: err.stack })
  error.value = `Error: ${err.message}`
  return false // Don't prevent error propagation
})

onMounted(() => {
  console.log('[ProjectSetup] Component mounted', { projectId: props.projectId })
  // Start change detection after mount
  nextTick(() => {
    console.log('[ProjectSetup] Starting change detection after mount')
    if (stepRef.value) {
      checkForChanges()
    }
    // Set up interval for change detection
    if (stepRef.value && typeof stepRef.value.hasChanges === 'function') {
      changeCheckInterval = setInterval(() => {
        if (stepRef.value && typeof stepRef.value.hasChanges === 'function') {
          try {
            checkForChanges()
          } catch (e) {
            console.error('[ProjectSetup] Error in checkForChanges interval', e)
            if (changeCheckInterval) {
              clearInterval(changeCheckInterval)
              changeCheckInterval = null
            }
          }
        } else {
          if (changeCheckInterval) {
            clearInterval(changeCheckInterval)
            changeCheckInterval = null
          }
        }
      }, 300)
    }
  })
})

const loading = ref(true)
const error = ref('')
const project = ref(null)
const saving = ref(false)
const currentStep = ref(0)
const loadingTimezones = ref(true)
const timezones = ref([])
const detectedTimezone = ref('')
const stepRef = ref(null)
const localeKey = ref(0)
const hasUnsavedChanges = ref(false)

// Computed: check if project has linked Telegram group
const hasTelegramGroup = computed(() => {
  return !!project.value?.telegramChatId
})

// Computed: check if project is personal
const isPersonalProject = computed(() => {
  return project.value?.isPersonal === true
})

// Computed property to track which component should be visible (for debugging)
const visibleComponent = computed(() => {
  if (currentStep.value === 0) return 'SetupBasicInfo'
  if (currentStep.value === 1) return 'SetupLanguageTimezone'
  if (currentStep.value === notificationsStep.value && hasTelegramGroup.value)
    return 'SetupNotifications'
  if (currentStep.value === silentHoursStep.value && hasTelegramGroup.value)
    return 'SetupSilentHours'
  if (currentStep.value === deadlineReportsStep.value && hasTelegramGroup.value)
    return 'SetupDeadlineReports'
  if (currentStep.value === completeStep.value) return 'SetupComplete'
  return 'Unknown'
})

// Helper functions to calculate step indices
// Base steps: Basic Info (0), Language & Timezone (1)
// Telegram steps (if has Telegram group): Notifications (2), Silent Hours (3), Deadline Reports (4), Completed Tasks Reports (5)
// Feature steps: Task Hierarchy, Time Tracking, Boards, Task Cloning, Task Templates, Checklists, Task Grades (if not personal)
// Complete step is last

const BASE_STEPS = 2 // Basic Info, Language & Timezone
const TELEGRAM_STEPS = 3 // Notifications, Silent Hours, Deadline Reports

// Computed: total steps
const totalSteps = computed(() => {
  const telegramSteps = hasTelegramGroup.value ? TELEGRAM_STEPS : 0
  return BASE_STEPS + telegramSteps + 1 // +1 for Complete step
})

// Computed properties for step indices (reactive)
const notificationsStep = computed(() => BASE_STEPS)
const silentHoursStep = computed(() => BASE_STEPS + 1)
const deadlineReportsStep = computed(() => BASE_STEPS + 2)

const completeStep = computed(() => {
  return totalSteps.value - 1
})

const projectUrl = computed(() => {
  let url = `/projects/${props.projectId}`
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const tasksUrl = computed(() => {
  let url = `/projects/${props.projectId}/dashboard`
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const breadcrumbsItems = computed(() => {
  try {
    const projectTitle = project.value?.title || s__('ProjectSetup|Project')
    return [
      { label: s__('Dashboard|Main'), url: dashboardUrl.value },
      { label: projectTitle, url: tasksUrl.value },
      { label: s__('ProjectSetup|Setup') },
    ]
  } catch (e) {
    console.error('[ProjectSetup] Error computing breadcrumbsItems', e)
    return [
      { label: s__('Dashboard|Main'), url: dashboardUrl.value },
      { label: s__('ProjectSetup|Project'), url: tasksUrl.value },
      { label: s__('ProjectSetup|Setup') },
    ]
  }
})

const dashboardUrl = computed(() => {
  let url = '/dashboard'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

// GraphQL queries
const PROJECT_Q = gql`
  query GetProjectForSetup($id: ID!) {
    currentUser {
      id
      preferredLanguage
    }
    project(id: $id) {
      id
      code
      slug
      title
      description
      preferredLanguage
      preferredTimezone
      gradesEnabled
      verticalRelationsEnabled
      timeTrackingEnabled
      taskCloningEnabled
      boardsEnabled
      templatesEnabled
      checklistsEnabled
      groupChatNotificationsEnabled
      silentHours
      deadlineReportTime
      deadlineReportDays
      completedTasksReportTime
      completedTasksReportDays
      isPersonal
      telegramChatId
      notificationPreferences
      canUpdateProject
      canUpdateTitle
      canUpdateDescription
    }
  }
`

const TIMEZONES_Q = gql`
  query GetTimezones {
    timezoneOptions {
      value
      label
    }
  }
`

// Fetch timezones
console.log('[ProjectSetup] Setting up timezones query')
let timezonesQueryResult
try {
  timezonesQueryResult = useQuery(TIMEZONES_Q)
  console.log('[ProjectSetup] Timezones query setup successful', timezonesQueryResult)
} catch (e) {
  console.error('[ProjectSetup] Error setting up timezones query', e)
  throw e
}
const { result: timezonesResult, loading: tzLoading } = timezonesQueryResult

watch(
  tzLoading,
  val => {
    console.log('[ProjectSetup] Timezones loading state changed', val)
    loadingTimezones.value = val
  },
  { immediate: true }
)

watch(
  timezonesResult,
  data => {
    if (data?.timezoneOptions) {
      timezones.value = data.timezoneOptions
    }
  },
  { immediate: true }
)

// Detect browser timezone
if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
  detectedTimezone.value = Intl.DateTimeFormat().resolvedOptions().timeZone
}

// Fetch project data
console.log('[ProjectSetup] Setting up project query', { projectId: props.projectId })

// Use ref for variables like tasks/Index.vue pattern
// Initialize with null to prevent query from running before projectId is available
const projectQueryVariables = ref(props.projectId ? { id: props.projectId } : null)

// Watch props.projectId to update variables reactively
watch(
  () => props.projectId,
  newId => {
    console.log('[ProjectSetup] projectId prop changed', newId)
    if (newId) {
      projectQueryVariables.value = { id: newId }
    } else {
      projectQueryVariables.value = null
    }
  },
  { immediate: true }
)

const {
  result: projectResult,
  loading: projectLoading,
  error: projectError,
  refetch: refetchProject,
} = useQuery(PROJECT_Q, projectQueryVariables, () => ({
  enabled: !!props.projectId && projectQueryVariables.value !== null,
  fetchPolicy: 'network-only',
}))

console.log('[ProjectSetup] Project query setup successful')

watch(
  projectLoading,
  val => {
    console.log('[ProjectSetup] Project loading state changed', val)
    loading.value = val
  },
  { immediate: true }
)

// Remove HTML loading screen when data is loaded
watch(loading, isLoading => {
  if (!isLoading) {
    removeInitialLoading()
  }
})

watch(
  projectError,
  val => {
    console.log('[ProjectSetup] Project error state changed', val)
    if (val) {
      const errorMsg = val.message || 'Failed to load project'
      console.error('[ProjectSetup] Project query error', errorMsg, val)
      error.value = errorMsg
    }
  },
  { immediate: true }
)

// Use a flag to track if component is ready for updates
const isComponentReady = ref(false)

// Track if we're currently processing a save to prevent duplicate step increments
const isProcessingSave = ref(false)

// Track the last step that successfully saved to prevent stale events
const lastSavedStep = ref(-1)

// Track when we last moved to a new step to detect stale events
const lastStepChangeTime = ref(Date.now())

// Track the most recently emitted save event step to prevent processing stale events
const lastEmittedSaveStep = ref(-1)

// Component ready flag is set in processProjectData after project data is loaded

watch(
  projectResult,
  data => {
    console.log('[ProjectSetup] Project result changed', data)
    processProjectData(data)
  },
  { immediate: true }
)

function processProjectData(data) {
  try {
    const projectData = data?.project
    if (projectData) {
      console.log('[ProjectSetup] Processing project data', projectData)

      // Check authorization - only owners and admins can access setup
      if (!projectData.canUpdateProject) {
        // Redirect to access denied page
        const params = []
        if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
        if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
        const queryString = params.length > 0 ? '?' + params.join('&') : ''
        window.location.href = `/projects/${props.projectId}/access_denied${queryString}`
        return
      }

      // Update project value
      project.value = projectData
      console.log('[ProjectSetup] Project value updated')

      // Load translations based on user's preferred language
      // The UI should be displayed in the user's preferred language, not the project's
      // The project's preferredLanguage is only for group chat notifications
      const userPreferredLanguage = data?.currentUser?.preferredLanguage || null
      if (userPreferredLanguage) {
        document.documentElement.lang = userPreferredLanguage
        loadTranslations(userPreferredLanguage)
        localeKey.value++ // Force re-render with new translations
        console.log('[ProjectSetup] Loaded translations for user language', userPreferredLanguage)
      }

      // Set component ready flag - Vue will process this reactively
      isComponentReady.value = false
      nextTick(() => {
        isComponentReady.value = true
        console.log('[ProjectSetup] Component marked as ready')
      })
    } else {
      console.log('[ProjectSetup] No project data in result')
      isComponentReady.value = false
    }
  } catch (e) {
    console.error('[ProjectSetup] Error processing project data', e)
    error.value = `Error processing project data: ${e.message}`
    isComponentReady.value = false
  }
}

function goToPreviousStep() {
  if (currentStep.value > 0) {
    console.log('[ProjectSetup] goToPreviousStep: decrementing step', {
      from: currentStep.value,
      to: currentStep.value - 1,
    })
    currentStep.value--
  }
}

function goToStep(stepIndex) {
  if (stepIndex >= 0 && stepIndex < totalSteps.value) {
    console.log('[ProjectSetup] goToStep: setting step', {
      from: currentStep.value,
      to: stepIndex,
    })
    currentStep.value = stepIndex
  }
}

function handleSkip() {
  if (currentStep.value < totalSteps.value - 1) {
    console.log('[ProjectSetup] handleSkip: incrementing step', {
      from: currentStep.value,
      to: currentStep.value + 1,
    })
    currentStep.value++
  }
}

async function handleStepSave(stepData) {
  // This function is only used by Setup components that still emit 'save' events
  // Step 1 and Step 2 now return data directly from save() method instead of emitting events
  // This eliminates the race condition that was causing stale events

  // Prevent duplicate calls
  if (isProcessingSave.value || saving.value) {
    return
  }

  isProcessingSave.value = true

  // CRITICAL FIX: Verify that stepData matches the current step
  // If stepData contains data from a different step, ignore it (stale event)
  const stepDataKeys = Object.keys(stepData || {})
  const isStep1Data = stepDataKeys.includes('title') || stepDataKeys.includes('description')
  const isStep2Data =
    stepDataKeys.includes('preferredLanguage') || stepDataKeys.includes('preferredTimezone')
  const isStep3Data =
    stepDataKeys.includes('groupChatNotificationsEnabled') ||
    stepDataKeys.includes('notificationPreferences')

  // Determine which step the data belongs to FIRST
  let dataStep = null
  if (isStep1Data && !isStep2Data && !isStep3Data) dataStep = 0
  else if (isStep2Data && !isStep1Data && !isStep3Data) dataStep = 1
  else if (isStep3Data && !isStep1Data && !isStep2Data) dataStep = notificationsStep.value

  // CRITICAL: Check if Step 2's save event was emitted AFTER determining dataStep
  // This ensures that if Step 2's event arrives first, Step 1's stale event will be ignored
  // We check this AFTER determining dataStep so we can use dataStep in the condition
  const step2EventEmitted =
    typeof window !== 'undefined' && window.__setupLanguageTimezoneSaveEmitted === true
  const step2EventTimestamp =
    typeof window !== 'undefined' ? window.__setupLanguageTimezoneSaveTimestamp || 0 : 0
  const currentTimestamp = Date.now()

  // Store check result in window for debugging
  if (typeof window !== 'undefined') {
    window.__projectSetupEarlyCheck = {
      step2EventEmitted,
      isStep1Data,
      isStep2Data,
      isStep3Data,
      stepDataKeys: stepDataKeys.slice(),
      dataStep,
      windowCheck: window.__setupLanguageTimezoneSaveEmitted,
      step2EventTimestamp,
      currentTimestamp,
      timestamp: currentTimestamp,
    }
  }

  console.log('[ProjectSetup] Early stale event check', {
    step2EventEmitted,
    isStep1Data,
    isStep2Data,
    isStep3Data,
    stepDataKeys,
    dataStep,
    windowCheck:
      typeof window !== 'undefined'
        ? window.__setupLanguageTimezoneSaveEmitted
        : 'window undefined',
    step2EventTimestamp,
    currentTimestamp,
  })

  // CRITICAL: Check if a more recent save event was already emitted BEFORE any step-specific checks
  // This ensures that if Step 2's event arrives first, Step 1's stale event will be ignored
  const previousLastEmitted = lastEmittedSaveStep.value

  // If a more recent save event was already emitted, ignore this older one
  if (dataStep !== null && previousLastEmitted > dataStep) {
    const warnMsg =
      '[ProjectSetup] Ignoring stale save event - a more recent save event was already emitted'
    console.warn(warnMsg, {
      currentStep: currentStep.value,
      dataStep,
      previousLastEmitted,
      lastEmittedSaveStep: lastEmittedSaveStep.value,
      lastSavedStep: lastSavedStep.value,
      stepDataKeys,
      reason:
        'A more recent save event was already emitted, so this is a delayed event from a previous step',
    })
    if (typeof window !== 'undefined') {
      window.__projectSetupStaleEventIgnored = {
        reason: 'lastEmittedSaveStepCheck',
        previousLastEmitted,
        dataStep,
      }
    }
    return
  }

  // CRITICAL: If we're getting Step 1's data, check multiple indicators to see if we're actually on Step 2
  // This catches stale Step 1 events that arrive after we've moved to Step 2
  if (dataStep === 0) {
    // PRIMARY CHECK: Check if we've already saved Step 2 (lastSavedStep >= 1)
    // This is the most reliable check - if we've already saved Step 2, Step 1's event is definitely stale
    if (lastSavedStep.value >= 1) {
      const warnMsg =
        '[ProjectSetup] Ignoring stale save event - Step 2 was already saved, but got Step 1 data'
      console.warn(warnMsg, {
        currentStep: currentStep.value,
        dataStep: 0,
        lastSavedStep: lastSavedStep.value,
        step2EventEmitted,
        step2EventTimestamp,
        currentTimestamp,
        lastEmittedSaveStep: lastEmittedSaveStep.value,
        stepDataKeys,
        reason: "Step 2 was already saved (lastSavedStep >= 1), so Step 1's stale event is ignored",
      })
      if (typeof window !== 'undefined') {
        window.__projectSetupStaleEventIgnored = {
          reason: 'step2AlreadySaved',
          dataStep: 0,
          lastSavedStep: lastSavedStep.value,
          step2EventEmitted: step2EventEmitted || step2EventTimestamp > 0,
          step2EventTimestamp,
          currentTimestamp,
          isStep1Data: true,
          isStep2Data: false,
          isStep3Data: false,
          timestamp: currentTimestamp,
        }
      }
      return
    }

    // SECONDARY CHECK: Check if Step 2's event was already emitted by checking the timestamp
    // Re-read it here to get the most current value (Step 2's event might have been emitted after we read it at the start)
    const currentStep2Timestamp =
      typeof window !== 'undefined' ? window.__setupLanguageTimezoneSaveTimestamp || 0 : 0

    // If Step 2's timestamp exists, Step 2's event was emitted, so Step 1's event is definitely stale
    if (currentStep2Timestamp > 0) {
      const warnMsg =
        "[ProjectSetup] Ignoring stale save event - Step 2's event was already emitted (timestamp check)"
      console.warn(warnMsg, {
        currentStep: currentStep.value,
        dataStep: 0,
        step2EventEmitted,
        step2EventTimestamp,
        currentStep2Timestamp,
        currentTimestamp,
        timeDiff: currentTimestamp - currentStep2Timestamp,
        lastEmittedSaveStep: lastEmittedSaveStep.value,
        lastSavedStep: lastSavedStep.value,
        stepDataKeys,
        reason: "Step 2's save event timestamp exists, so Step 1's stale event is ignored",
      })
      if (typeof window !== 'undefined') {
        window.__projectSetupStaleEventIgnored = {
          reason: 'step2TimestampExists',
          dataStep: 0,
          step2EventEmitted: false, // May be false due to race condition
          step2EventTimestamp,
          currentStep2Timestamp,
          currentTimestamp,
          timeDiff: currentTimestamp - currentStep2Timestamp,
          isStep1Data: true,
          isStep2Data: false,
          isStep3Data: false,
          timestamp: currentTimestamp,
        }
      }
      return
    }

    // SECONDARY CHECK: Read the progress indicator from the DOM to get the actual displayed step
    // This is a backup check in case the timestamp check didn't catch it
    const progressIndicator = document.querySelector('.progress-indicator')
    let displayedStep = null
    if (progressIndicator) {
      const progressText = progressIndicator.textContent || ''
      const stepMatch = progressText.match(/(?:Step|Шаг)\s+(\d+)/i)
      if (stepMatch) {
        displayedStep = parseInt(stepMatch[1], 10) - 1 // Convert to 0-based index
      }
    }

    // If the progress indicator shows we're on Step 2 or later, ignore Step 1's stale event
    if (displayedStep !== null && displayedStep >= 1) {
      const warnMsg =
        '[ProjectSetup] Ignoring stale save event - progress indicator shows Step 2 or later, but got Step 1 data'
      console.warn(warnMsg, {
        currentStep: currentStep.value,
        displayedStep,
        dataStep: 0,
        step2EventEmitted,
        step2EventTimestamp,
        currentTimestamp,
        lastEmittedSaveStep: lastEmittedSaveStep.value,
        lastSavedStep: lastSavedStep.value,
        stepDataKeys,
        reason:
          "Progress indicator shows we're on Step 2 or later, so Step 1's stale event is ignored",
      })
      if (typeof window !== 'undefined') {
        window.__projectSetupStaleEventIgnored = {
          reason: 'progressIndicatorCheck',
          dataStep: 0,
          displayedStep,
          step2EventEmitted: step2EventEmitted || step2EventTimestamp > 0,
          step2EventTimestamp,
          currentTimestamp,
          isStep1Data: true,
          isStep2Data: false,
          isStep3Data: false,
          timestamp: currentTimestamp,
        }
      }
      return
    }

    // SECONDARY CHECK: Read currentStep directly from the ref to get the actual current value
    // This is a backup check in case the progress indicator hasn't updated yet
    const actualCurrentStep = currentStep.value
    if (actualCurrentStep >= 1) {
      // We're on Step 2 or later, so Step 1's event is definitely stale
      const warnMsg =
        '[ProjectSetup] Ignoring stale save event - actually on Step 2 or later, but got Step 1 data'
      console.warn(warnMsg, {
        currentStep: currentStep.value,
        actualCurrentStep,
        dataStep: 0,
        step2EventEmitted,
        step2EventTimestamp,
        currentTimestamp,
        lastEmittedSaveStep: lastEmittedSaveStep.value,
        lastSavedStep: lastSavedStep.value,
        stepDataKeys,
        reason:
          "currentStep indicates we're on Step 2 or later, so Step 1's stale event is ignored",
      })
      if (typeof window !== 'undefined') {
        window.__projectSetupStaleEventIgnored = {
          reason: 'actualCurrentStepCheck',
          dataStep: 0,
          actualCurrentStep,
          step2EventEmitted: step2EventEmitted || step2EventTimestamp > 0,
          step2EventTimestamp,
          currentTimestamp,
          isStep1Data: true,
          isStep2Data: false,
          isStep3Data: false,
          timestamp: currentTimestamp,
        }
      }
      return
    }

    // The timestamp check was already done above as the PRIMARY CHECK, so we don't need to check it again here
    // If we reach this point, the timestamp check didn't catch it, so we continue with other checks

    // Check 1: Read currentStep directly from the ref (not from closure) to get the actual current value
    // (actualCurrentStep was already read above, reuse it)

    // Check 2: Check which step component is actually visible in the DOM
    const stepContent = document.querySelector('.step-content')
    let visibleStepIndex = null

    if (stepContent) {
      // Get all h3 elements (step titles)
      const allStepTitles = stepContent.querySelectorAll('h3')
      // Find the one that's actually visible (not hidden by v-if or display:none)
      for (const stepTitle of allStepTitles) {
        const style = window.getComputedStyle(stepTitle)
        const isVisible =
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          stepTitle.offsetParent !== null
        if (isVisible) {
          const titleText = stepTitle.textContent || ''
          // Match step titles like "Step 1: Basic Information" or "Шаг 2: Язык и часовой пояс"
          const stepMatch = titleText.match(/(?:Step|Шаг)\s+(\d+)/i)
          if (stepMatch) {
            visibleStepIndex = parseInt(stepMatch[1], 10) - 1 // Convert to 0-based index
            break // Found the visible one, stop looking
          }
        }
      }
    }

    // Check 3: Reuse displayedStep from the primary check above
    // (displayedStep was already read in the PRIMARY CHECK, reuse it)

    // If ANY of these checks indicate we're on Step 2 or later, ignore Step 1's stale event
    if (
      actualCurrentStep >= 1 ||
      (visibleStepIndex !== null && visibleStepIndex >= 1) ||
      (displayedStep !== null && displayedStep >= 1)
    ) {
      const warnMsg =
        '[ProjectSetup] Ignoring stale save event - actually on Step 2 or later, but got Step 1 data'
      console.warn(warnMsg, {
        currentStep: currentStep.value,
        actualCurrentStep,
        dataStep: 0,
        visibleStepIndex,
        displayedStep,
        step2EventEmitted,
        step2EventTimestamp,
        currentTimestamp,
        lastEmittedSaveStep: lastEmittedSaveStep.value,
        lastSavedStep: lastSavedStep.value,
        stepDataKeys,
        reason:
          "Multiple checks indicate we're on Step 2 or later, so Step 1's stale event is ignored",
      })
      if (typeof window !== 'undefined') {
        window.__projectSetupStaleEventIgnored = {
          reason: 'multipleChecksIndicateStep2',
          dataStep: 0,
          actualCurrentStep,
          visibleStepIndex,
          displayedStep,
          step2EventEmitted: step2EventEmitted || step2EventTimestamp > 0,
          step2EventTimestamp,
          currentTimestamp,
          isStep1Data: true,
          isStep2Data: false,
          isStep3Data: false,
          timestamp: currentTimestamp,
        }
      }
      return
    }

    // Also check if Step 2's event was already emitted (backup check)
    if (step2EventEmitted) {
      const warnMsg =
        "[ProjectSetup] Ignoring stale save event - Step 2's event was already emitted"
      console.warn(warnMsg, {
        currentStep: currentStep.value,
        dataStep: 0,
        step2EventEmitted,
        step2EventTimestamp,
        currentTimestamp,
        timeDiff: step2EventTimestamp > 0 ? currentTimestamp - step2EventTimestamp : null,
        lastEmittedSaveStep: lastEmittedSaveStep.value,
        lastSavedStep: lastSavedStep.value,
        stepDataKeys,
        reason: "Step 2's save event was already emitted, so Step 1's stale event is ignored",
      })
      if (typeof window !== 'undefined') {
        window.__projectSetupStaleEventIgnored = {
          reason: 'step2EventAlreadyEmitted',
          dataStep: 0,
          step2EventEmitted: true,
          step2EventTimestamp,
          currentTimestamp,
          timeDiff: step2EventTimestamp > 0 ? currentTimestamp - step2EventTimestamp : null,
          isStep1Data: true,
          isStep2Data: false,
          isStep3Data: false,
          timestamp: currentTimestamp,
        }
      }
      return
    }
  }

  console.log('[ProjectSetup] Checking for stale events', {
    dataStep,
    previousLastEmitted,
    step2EventEmitted,
    stepDataKeys,
    isStep1Data,
    isStep2Data,
    isStep3Data,
  })

  // Update lastEmittedSaveStep IMMEDIATELY when event is received (after validation check above)
  // This ensures that if Step 2's event arrives first, Step 1's stale event will be ignored
  if (dataStep !== null) {
    lastEmittedSaveStep.value = dataStep
    console.log('[ProjectSetup] Updated lastEmittedSaveStep', {
      from: previousLastEmitted,
      to: dataStep,
      stepDataKeys,
    })
  }

  // CRITICAL FIX: If this is data from a step we've already moved past, ignore it (stale/delayed event)
  // This prevents Step 1's delayed save event from being processed when we're on Step 2
  // Check both currentStep and lastSavedStep to catch stale events even if currentStep hasn't updated yet
  if (stepDataKeys.length > 0 && dataStep !== null) {
    // SECOND: If we're currently on a step that's past the data's step, ignore it
    // This is the most direct check - if currentStep > dataStep, we've moved past that step
    if (currentStep.value > dataStep) {
      const warnMsg =
        "[ProjectSetup] Ignoring stale save event - data from step we've already moved past"
      console.warn(warnMsg, {
        currentStep: currentStep.value,
        dataStep,
        lastSavedStep: lastSavedStep.value,
        stepDataKeys,
        reason: "This is a delayed event from a previous step that we've already moved past",
      })
      if (typeof window !== 'undefined') {
        window.__projectSetupStaleEventIgnored = {
          reason: 'currentStepCheck',
          currentStep: currentStep.value,
          dataStep,
        }
      }
      return
    }

    // THIRD: If we've already saved and moved past this step, ignore it (most reliable check)
    // This catches stale events even if currentStep hasn't updated yet
    if (lastSavedStep.value >= dataStep) {
      const warnMsg =
        "[ProjectSetup] Ignoring stale save event - data from step we've already saved and passed"
      console.warn(warnMsg, {
        currentStep: currentStep.value,
        dataStep,
        lastSavedStep: lastSavedStep.value,
        stepDataKeys,
        reason: "This is a delayed event from a step we've already saved and moved past",
      })
      if (typeof window !== 'undefined') {
        window.__projectSetupStaleEventIgnored = {
          reason: 'lastSavedStepCheck',
          lastSavedStep: lastSavedStep.value,
          dataStep,
        }
      }
      return
    }

    // SECOND: Check the progress indicator - this is what the user sees
    const progressIndicator = document.querySelector('.progress-indicator')
    let displayedStep = null
    if (progressIndicator) {
      const progressText = progressIndicator.textContent || ''
      const stepMatch = progressText.match(/(?:Step|Шаг)\s+(\d+)/i)
      if (stepMatch) {
        displayedStep = parseInt(stepMatch[1], 10) - 1 // Convert to 0-based index
      }
    }

    // If the progress indicator shows we're on a step that's past the data's step, this is a stale event
    if (displayedStep !== null && displayedStep > dataStep) {
      const warnMsg =
        "[ProjectSetup] Ignoring stale save event - progress indicator shows we're on a later step"
      console.warn(warnMsg, {
        currentStep: currentStep.value,
        displayedStep,
        dataStep,
        lastSavedStep: lastSavedStep.value,
        stepDataKeys,
        reason:
          "Progress indicator shows we're on a later step, so this is a delayed event from a previous step",
      })
      if (typeof window !== 'undefined') {
        window.__projectSetupStaleEventIgnored = {
          reason: 'progressStepMismatch',
          displayedStep,
          dataStep,
        }
        window.__projectSetupDomCheckResult = {
          displayedStep,
          dataStep,
          currentStep: currentStep.value,
          progressText: progressIndicator?.textContent || null,
        }
      }
      return
    }

    // SECOND: Check which step component is actually visible in the DOM
    // Find all h3 elements in step-content and check which one is visible (not hidden)
    const stepContent = document.querySelector('.step-content')
    let visibleStepIndex = null

    if (stepContent) {
      // Get all h3 elements
      const allStepTitles = stepContent.querySelectorAll('h3')
      // Find the one that's actually visible (not hidden by v-if or display:none)
      for (const stepTitle of allStepTitles) {
        const style = window.getComputedStyle(stepTitle)
        const isVisible =
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          stepTitle.offsetParent !== null
        if (isVisible) {
          const titleText = stepTitle.textContent || ''
          // Match step titles like "Step 1: Basic Information" or "Шаг 2: Язык и часовой пояс"
          const stepMatch = titleText.match(/(?:Step|Шаг)\s+(\d+)/i)
          if (stepMatch) {
            visibleStepIndex = parseInt(stepMatch[1], 10) - 1 // Convert to 0-based index
            break // Found the visible one, stop looking
          }
        }
      }
    }

    // Log what we found for debugging (also store in window for test access)
    const domCheckResult = {
      visibleStepIndex,
      displayedStep,
      dataStep,
      stepContent: !!stepContent,
      currentStep: currentStep.value,
    }
    console.log('[ProjectSetup] DOM check result', domCheckResult)
    if (typeof window !== 'undefined') {
      window.__projectSetupDomCheckResult = domCheckResult
    }

    // If we found a visible step and it's different from the data's step, this is a stale event
    if (visibleStepIndex !== null && visibleStepIndex !== dataStep) {
      const warnMsg =
        "[ProjectSetup] Ignoring stale save event - visible step doesn't match data step"
      console.warn(warnMsg, {
        currentStep: currentStep.value,
        visibleStepIndex,
        dataStep,
        lastSavedStep: lastSavedStep.value,
        stepDataKeys,
        reason:
          "The visible step component doesn't match the data's step, so this is a delayed event from a previous step",
      })
      if (typeof window !== 'undefined') {
        window.__projectSetupStaleEventIgnored = {
          reason: 'visibleStepMismatch',
          visibleStepIndex,
          dataStep,
        }
      }
      return
    }
  }

  // If stepData doesn't match current step, ignore it (stale event)
  // This is the key fix: ignore events from different steps
  if (stepDataKeys.length > 0 && dataStep !== null && dataStep !== currentStep.value) {
    console.warn('[ProjectSetup] Ignoring stale save event', {
      currentStep: currentStep.value,
      dataStep,
      lastSavedStep: lastSavedStep.value,
      stepDataKeys,
      reason: 'Data from different step - this is a delayed event from a previous step',
    })
    return
  }

  // Additional safety checks for common stale event patterns
  // If we're on Step 2 (currentStep === 1) but got Step 1 data, definitely ignore it
  if (currentStep.value === 1 && isStep1Data && !isStep2Data) {
    console.warn(
      '[ProjectSetup] Ignoring stale save event - on Step 2 but got Step 1 data (delayed event)'
    )
    return
  }

  // If we're on Step 1 (currentStep === 0) but got Step 2 data, ignore it (shouldn't happen)
  if (currentStep.value === 0 && isStep2Data && !isStep1Data) {
    console.warn(
      '[ProjectSetup] Ignoring stale save event - on Step 1 but got Step 2 data (future event?)'
    )
    return
  }

  // lastEmittedSaveStep was already updated above when the event was received
  // No need to update it again here

  isProcessingSave.value = true
  const stepBeforeIncrement = currentStep.value

  try {
    // If language was changed, update locale immediately
    if (stepData.preferredLanguage !== undefined) {
      const newLanguage = stepData.preferredLanguage
      if (newLanguage) {
        document.documentElement.lang = newLanguage
        loadTranslations(newLanguage)
      } else {
        const detectedLocale = document.documentElement.lang || 'en'
        loadTranslations(detectedLocale)
      }
      localeKey.value++
      // Wait for locale re-render to complete
      await nextTick()
    }

    // Wait for project refetch to complete (if it's happening)
    // Give it time to finish processing
    await new Promise(resolve => setTimeout(resolve, 150))

    // Check if step indices changed after refetch
    console.log('[ProjectSetup] After refetch delay', {
      currentStep: currentStep.value,
      hasTelegramGroup: hasTelegramGroup.value,
      notificationsStep: notificationsStep.value,
      totalSteps: totalSteps.value,
    })

    // Wait for any other re-renders to complete before advancing step
    await nextTick()
    await nextTick() // Double nextTick to ensure all updates are complete

    // Verify we're still on the same step (in case something changed it)
    if (currentStep.value !== stepBeforeIncrement) {
      console.warn('[ProjectSetup] Step changed during save processing', {
        stepBeforeIncrement,
        currentStep: currentStep.value,
      })
      // Don't increment if step already changed
      return
    }

    // Step saved successfully, advance to next step
    // Use a single atomic increment to prevent race conditions
    const targetStep = stepBeforeIncrement + 1
    if (targetStep < totalSteps.value) {
      console.log('[ProjectSetup] Incrementing step', {
        from: currentStep.value,
        to: targetStep,
        totalSteps: totalSteps.value,
        hasTelegramGroup: hasTelegramGroup.value,
        notificationsStep: notificationsStep.value,
      })
      // Set directly to target step to ensure atomic operation
      currentStep.value = targetStep
      // Update lastSavedStep to track that we've successfully saved and moved past this step
      lastSavedStep.value = stepBeforeIncrement
      // Update lastStepChangeTime to track when we moved to this step
      lastStepChangeTime.value = Date.now()
      console.log('[ProjectSetup] Step set to', currentStep.value, {
        expectedComponent: visibleComponent.value,
        targetStep,
        lastSavedStep: lastSavedStep.value,
        lastStepChangeTime: lastStepChangeTime.value,
        notificationsStep: notificationsStep.value,
        silentHoursStep: silentHoursStep.value,
        hasTelegramGroup: hasTelegramGroup.value,
      })

      // Wait for step change to propagate
      await nextTick()
      await nextTick()
    } else {
      console.log('[ProjectSetup] Cannot increment step - already at last step', {
        currentStep: currentStep.value,
        totalSteps: totalSteps.value,
      })
    }
  } finally {
    // Reset flag after ensuring all operations are complete
    await nextTick()
    await nextTick() // Double nextTick to ensure step change is fully processed
    const finalStep = currentStep.value
    console.log('[ProjectSetup] Resetting isProcessingSave flag', {
      stepBeforeIncrement,
      currentStep: finalStep,
      hasTelegramGroup: hasTelegramGroup.value,
      notificationsStep: notificationsStep.value,
      silentHoursStep: silentHoursStep.value,
    })
    isProcessingSave.value = false

    // Final verification: check if step changed after reset
    await nextTick()
    if (currentStep.value !== finalStep) {
      console.error('[ProjectSetup] CRITICAL: Step changed after isProcessingSave reset!', {
        expected: finalStep,
        actual: currentStep.value,
        diff: currentStep.value - finalStep,
      })
    } else {
      console.log('[ProjectSetup] Step verified stable after reset', {
        step: currentStep.value,
      })
    }
  }
}

// Function to check for changes in current step
function checkForChanges() {
  try {
    if (!stepRef.value) {
      console.log('[ProjectSetup] checkForChanges: stepRef.value is null/undefined')
      hasUnsavedChanges.value = false
      return
    }
    if (typeof stepRef.value.hasChanges !== 'function') {
      console.log('[ProjectSetup] checkForChanges: hasChanges is not a function', stepRef.value)
      hasUnsavedChanges.value = false
      return
    }
    hasUnsavedChanges.value = stepRef.value.hasChanges()
  } catch (e) {
    console.error('[ProjectSetup] Error in checkForChanges', e)
    hasUnsavedChanges.value = false
  }
}

// Periodically check for changes
let changeCheckInterval = null

// Watch currentStep to check for changes when step changes
watch(currentStep, (newStep, oldStep) => {
  console.log('[ProjectSetup] currentStep changed', {
    from: oldStep,
    to: newStep,
    isProcessingSave: isProcessingSave.value,
    hasTelegramGroup: hasTelegramGroup.value,
    notificationsStep: notificationsStep.value,
    silentHoursStep: silentHoursStep.value,
    expectedComponent:
      newStep === notificationsStep.value
        ? 'Notifications'
        : newStep === silentHoursStep.value
          ? 'SilentHours'
          : newStep === deadlineReportsStep.value
            ? 'DeadlineReports'
            : newStep === completeStep.value
              ? 'Complete'
              : 'Unknown',
  })

  // Prevent any side effects during save processing
  if (isProcessingSave.value) {
    // During save processing, only allow incrementing by 1
    if (newStep !== oldStep + 1) {
      console.warn('[ProjectSetup] Unexpected step change during save processing', {
        from: oldStep,
        to: newStep,
        expected: oldStep + 1,
      })
      // If step was incremented by more than 1 during save, restore it
      if (newStep > oldStep + 1) {
        console.warn('[ProjectSetup] Restoring step to expected value', oldStep + 1)
        currentStep.value = oldStep + 1
        return // Don't continue with watcher logic
      }
    }
  }

  nextTick(() => {
    // Only check if component is mounted and stepRef exists
    // Skip if we're processing a save to avoid interference
    if (
      !isProcessingSave.value &&
      stepRef.value &&
      typeof stepRef.value.hasChanges === 'function'
    ) {
      try {
        checkForChanges()
      } catch (e) {
        console.error('[ProjectSetup] Error checking changes on step change', e)
      }
    }
  })
})

// Watch stepRef to set up change detection when it becomes available
watch(
  () => stepRef.value,
  newRef => {
    console.log('[ProjectSetup] stepRef changed', {
      hasRef: !!newRef,
      hasMethod: newRef && typeof newRef.hasChanges === 'function',
    })

    // Clear existing interval
    if (changeCheckInterval) {
      clearInterval(changeCheckInterval)
      changeCheckInterval = null
    }

    // Set up new interval if ref is available
    if (newRef && typeof newRef.hasChanges === 'function') {
      try {
        checkForChanges()
        changeCheckInterval = setInterval(() => {
          if (stepRef.value && typeof stepRef.value.hasChanges === 'function') {
            try {
              checkForChanges()
            } catch (e) {
              console.error('[ProjectSetup] Error in checkForChanges interval', e)
              if (changeCheckInterval) {
                clearInterval(changeCheckInterval)
                changeCheckInterval = null
              }
            }
          } else {
            if (changeCheckInterval) {
              clearInterval(changeCheckInterval)
              changeCheckInterval = null
            }
          }
        }, 300)
      } catch (e) {
        console.error('[ProjectSetup] Error setting up change detection', e)
        hasUnsavedChanges.value = false
      }
    } else {
      hasUnsavedChanges.value = false
    }
  }
)

async function handleNext() {
  if (currentStep.value < totalSteps.value - 1) {
    console.log('[ProjectSetup] handleNext: incrementing step', {
      from: currentStep.value,
      to: currentStep.value + 1,
    })
    currentStep.value++
  }
}

async function handleSaveAndContinue() {
  if (!stepRef.value || saving.value) {
    return
  }

  saving.value = true
  error.value = ''
  isProcessingSave.value = true

  try {
    const result = await stepRef.value.save()

    // If save() returns false, it failed
    if (result === false) {
      return
    }

    // Save succeeded - process the returned data
    hasUnsavedChanges.value = false

    // Note: We don't change UI locale when project's preferredLanguage is updated
    // The UI should remain in the user's preferred language
    // The project's preferredLanguage is only for group chat notifications

    // Wait for project refetch to complete (if it's happening)
    // This ensures step indices are stable before advancing
    await new Promise(resolve => setTimeout(resolve, 100))

    // Wait for any re-renders to complete before advancing step
    await nextTick()

    // Advance to next step after successful save
    const stepBeforeIncrement = currentStep.value
    let targetStep = stepBeforeIncrement + 1

    console.log('[ProjectSetup] handleSaveAndContinue: advancing step', {
      stepBeforeIncrement,
      targetStep,
      totalSteps: totalSteps.value,
      hasTelegramGroup: hasTelegramGroup.value,
      notificationsStep: notificationsStep.value,
      silentHoursStep: silentHoursStep.value,
    })

    if (targetStep < totalSteps.value) {
      currentStep.value = targetStep
      console.log('[ProjectSetup] handleSaveAndContinue: step advanced to', currentStep.value, {
        expectedComponent: visibleComponent.value,
        hasTelegramGroup: hasTelegramGroup.value,
        notificationsStep: notificationsStep.value,
      })

      // Wait for step change and any reactive updates to complete
      await nextTick()
      await nextTick()

      // Verify we're on the expected step after updates
      const actualStep = currentStep.value
      if (actualStep !== targetStep) {
        console.warn('[ProjectSetup] handleSaveAndContinue: step changed after nextTick', {
          expected: targetStep,
          actual: actualStep,
        })
        // If step changed unexpectedly, restore it
        if (actualStep > targetStep) {
          console.warn(
            '[ProjectSetup] handleSaveAndContinue: step was incremented unexpectedly, restoring to',
            targetStep
          )
          currentStep.value = targetStep
          await nextTick()
        }
      }
    } else {
      console.log('[ProjectSetup] handleSaveAndContinue: cannot advance - already at last step')
    }
  } catch (err) {
    console.error('[ProjectSetup] handleSaveAndContinue: error', err)
    error.value = err.message || 'Failed to save'
  } finally {
    saving.value = false
    isProcessingSave.value = false
  }
}

function handleResetChanges() {
  if (!stepRef.value || typeof stepRef.value.reset !== 'function') return

  stepRef.value.reset()
  hasUnsavedChanges.value = false
}

// Expose refetch for step components
defineExpose({
  refetchProject: () => refetchProject({ id: props.projectId }),
})
</script>

<style scoped>
.setup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.setup-header h2 {
  margin: 0;
}

.progress-indicator {
  color: #666;
  font-size: 14px;
}

.step-content {
  min-height: 400px;
  margin-bottom: 24px;
}

.step-navigation {
  display: flex;
  gap: 12px;
  align-items: center;
  padding-top: 24px;
  border-top: 1px solid #eee;
}

.spacer {
  flex: 1;
}

.btn-primary,
.btn-secondary {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background-color: #1d6bf3;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #1557c7;
}

.btn-primary:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #333;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e5e5e5;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.error {
  color: #d32f2f;
  font-size: 14px;
}

.muted {
  color: #999;
  font-size: 14px;
}
</style>
